
import { Ingredient, MeasurementUnit } from "@/types/recipe";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Minus, Plus } from "lucide-react";
import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface IngredientsListProps {
  ingredients: Ingredient[];
  onIngredientChange?: (index: number, ingredient: Ingredient) => void;
  onAddIngredient?: () => void;
  onRemoveIngredient?: (index: number) => void;
  defaultServings?: number;
  onServingsChange?: (servings: number) => void;
}

const UNITS: MeasurementUnit[] = [
  'teaspoon',
  'tablespoon',
  'cup',
  'ounce',
  'pound',
  'gram',
  'kilogram',
  'milliliter',
  'liter',
  'pinch',
  'piece',
  'slice',
  'whole'
];

const IngredientsList = ({ 
  ingredients,
  onIngredientChange,
  onAddIngredient,
  onRemoveIngredient,
  defaultServings = 1,
  onServingsChange
}: IngredientsListProps) => {
  const [servings, setServings] = useState(defaultServings);

  const adjustQuantity = (amount: number) => {
    return Number((amount * (servings / defaultServings)).toFixed(2));
  };

  const handleServingsChange = (newServings: number) => {
    setServings(newServings);
    if (onServingsChange) {
      onServingsChange(newServings);
    }
  };

  const getUnitDisplay = (unit: MeasurementUnit, amount: number) => {
    const spaceBeforeUnits = new Set(['teaspoon', 'tablespoon', 'whole']);
    
    let displayText = '';
    
    switch (unit) {
      case 'teaspoon':
        displayText = 'tsp';
        break;
      case 'tablespoon':
        displayText = 'tbsp';
        break;
      case 'gram':
        displayText = 'g';
        break;
      case 'kilogram':
        displayText = 'kg';
        break;
      case 'milliliter':
        displayText = 'ml';
        break;
      case 'liter':
        displayText = 'L';
        break;
      case 'ounce':
        displayText = 'oz';
        break;
      case 'pound':
        displayText = 'lb';
        break;
      case 'piece':
      case 'whole':
        displayText = 'pcs';
        break;
      default:
        displayText = amount === 1 ? unit : unit + 's';
    }

    return spaceBeforeUnits.has(unit) ? ' ' + displayText : displayText;
  };

  if (!onIngredientChange) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between p-2 bg-gray-50/50 rounded-lg">
          <span className="text-sm font-medium text-gray-700">Servings</span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => handleServingsChange(Math.max(1, servings - 1))}
              disabled={servings <= 1}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="w-8 text-center font-medium">{servings}</span>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => handleServingsChange(servings + 1)}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <ul className="space-y-3">
          {ingredients.map((ingredient, index) => (
            <li
              key={index}
              className="flex items-center justify-between text-gray-700 hover:bg-white/50 p-2 rounded-lg transition-colors duration-200 group"
            >
              <span className="font-medium group-hover:text-primary transition-colors">
                {ingredient.name}
              </span>
              <span className="text-gray-500 tabular-nums text-right min-w-[80px]">
                {adjustQuantity(ingredient.amount)}{getUnitDisplay(ingredient.unit, adjustQuantity(ingredient.amount))}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {ingredients.map((ingredient, index) => (
        <div key={index} className="flex gap-2">
          <div className="flex-1">
            <Input
              value={ingredient.name}
              onChange={(e) => onIngredientChange(index, { ...ingredient, name: e.target.value })}
              placeholder="Ingredient name"
            />
          </div>
          <div className="w-24">
            <Input
              type="number"
              value={ingredient.amount}
              onChange={(e) => onIngredientChange(index, { ...ingredient, amount: Number(e.target.value) })}
              placeholder="Amount"
              min="0"
              step="0.1"
              className="text-right"
            />
          </div>
          <div className="w-32">
            <Select
              value={ingredient.unit}
              onValueChange={(value: MeasurementUnit) => 
                onIngredientChange(index, { ...ingredient, unit: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Unit" />
              </SelectTrigger>
              <SelectContent>
                {UNITS.map((unit) => (
                  <SelectItem key={unit} value={unit}>
                    {getUnitDisplay(unit, 1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {onRemoveIngredient && ingredients.length > 1 && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => onRemoveIngredient(index)}
            >
              <Minus className="w-4 h-4" />
            </Button>
          )}
        </div>
      ))}
      {onAddIngredient && (
        <Button
          type="button"
          variant="outline"
          onClick={onAddIngredient}
          className="w-full"
        >
          Add Ingredient
        </Button>
      )}
    </div>
  );
};

export default IngredientsList;
